import { useState, useEffect, useRef } from 'react';
import { useToast } from '@/contexts/ToastContext';
import { supabase } from '@/lib/supabase';
import type { MenuItem, Category, Subcategory } from '@/types/common';

export interface UseMenuDataReturnType {
  menuItems: MenuItem[];
  categories: Category[];
  subcategories: Subcategory[];
  loading: boolean;
  createItem: (data: Omit<MenuItem, 'id' | 'created_at'>) => Promise<void>;
  updateItem: (id: string, data: Omit<MenuItem, 'id' | 'created_at'>) => Promise<void>;
  deleteItem: (id: string) => Promise<void>;
  toggleItemAvailability: (item: MenuItem) => Promise<void>;
}

interface RawMenuItem {
  id: string;
  name: string;
  description: string | null;
  price: number;
  category_id: string;
  subcategory_id: string | null;
  active: boolean;
  created_at: string;
}

const isValidMenuItem = (item: any): item is RawMenuItem => {
  return (
    item &&
    typeof item.id === 'string' &&
    typeof item.name === 'string' &&
    typeof item.price === 'number' &&
    typeof item.category_id === 'string' &&
    typeof item.active === 'boolean' &&
    typeof item.created_at === 'string'
  );
};

// Convert null to undefined for optional fields
const transformMenuItem = (item: any): MenuItem => ({
  ...item,
  description: item.description || undefined,
  subcategory_id: item.subcategory_id || undefined,
  subcategory: item.subcategory || undefined
});

export function useMenuData(isAuthLoading: boolean): UseMenuDataReturnType {
  const { addToast } = useToast();
  const [menuItems, setMenuItems] = useState<MenuItem[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [subcategories, setSubcategories] = useState<Subcategory[]>([]);
  const [loading, setLoading] = useState(true);
  const channelRef = useRef<ReturnType<typeof supabase.channel> | null>(null);

  const fetchData = async () => {
    try {
      setLoading(true);
      console.debug('Starting data fetch...');

      // Get categories and subcategories first
      console.debug('Fetching categories and subcategories...');
      const [
        { data: categoriesData, error: categoriesError },
        { data: subcategoriesData, error: subcategoriesError }
      ] = await Promise.all([
        supabase
          .from('categories')
          .select('id, name, icon, order_index, created_at')
          .order('order_index'),
        supabase
          .from('subcategories')
          .select('id, name, category_id, order_index, created_at')
          .order('order_index')
      ]);

      if (categoriesError) throw categoriesError;
      if (subcategoriesError) throw subcategoriesError;

      if (!categoriesData || categoriesData.length === 0) {
        throw new Error('No categories found in the database');
      }

      // Create category and subcategory lookup maps
      const categoryMap = new Map<string, Category>();
      const subcategoryMap = new Map<string, Subcategory>();

      categoriesData.forEach(category => {
        if (!category.id) {
          console.error('Category missing ID:', category);
          return;
        }
        categoryMap.set(category.id.trim(), category as Category);
      });

      subcategoriesData?.forEach(subcategory => {
        if (!subcategory.id) {
          console.error('Subcategory missing ID:', subcategory);
          return;
        }
        subcategoryMap.set(subcategory.id.trim(), subcategory as Subcategory);
      });

      // Debug the categories
      console.debug('Categories loaded:', {
        categories: categoriesData.map(c => ({
          id: c.id,
          name: c.name,
          idLength: c.id?.length
        })),
        mapKeys: Array.from(categoryMap.keys()).map(key => ({
          key,
          length: key.length,
          chars: Array.from(key).map(ch => ch.charCodeAt(0))
        }))
      });

      // Get menu items
      const { data: menuItemsData, error: menuItemsError } = await supabase
        .from('menu_items')
        .select('id, name, description, price, category_id, subcategory_id, active, created_at')
        .order('name');

      if (menuItemsError) throw menuItemsError;

      // Process menu items
      const processedItems: MenuItem[] = [];

      for (const item of menuItemsData || []) {
        if (!isValidMenuItem(item)) {
          console.warn('Invalid menu item:', item);
          continue;
        }

        const cleanCategoryId = item.category_id.trim();
        const category = categoryMap.get(cleanCategoryId);

        // Log every category lookup attempt
        console.debug(`Category lookup for "${item.name}":`, {
          rawCategoryId: item.category_id,
          cleanCategoryId,
          found: !!category,
          mapKeys: Array.from(categoryMap.keys()),
          mapContainsKey: categoryMap.has(cleanCategoryId),
          comparisons: Array.from(categoryMap.keys()).map(key => ({
            mapKey: key,
            lookupKey: cleanCategoryId,
            matches: key === cleanCategoryId,
            keyLength: key.length,
            lookupLength: cleanCategoryId.length,
            keyChars: Array.from(key).map(ch => ch.charCodeAt(0)),
            lookupChars: Array.from(cleanCategoryId).map(ch => ch.charCodeAt(0))
          }))
        });

        if (!category) {
          console.error(`Category not found for "${item.name}":`, {
            categoryId: cleanCategoryId
          });
          continue;
        }

        // Get subcategory if it exists
        const cleanSubcategoryId = item.subcategory_id?.trim() || null;
        const subcategory = cleanSubcategoryId ? subcategoryMap.get(cleanSubcategoryId) : undefined;

        if (cleanSubcategoryId && !subcategory) {
          console.warn(`Subcategory not found for "${item.name}":`, {
            subcategoryId: cleanSubcategoryId
          });
        }

        processedItems.push(transformMenuItem({
          id: item.id,
          name: item.name,
          description: item.description,
          price: item.price,
          category_id: cleanCategoryId,
          subcategory_id: cleanSubcategoryId,
          active: item.active,
          created_at: item.created_at,
          category,
          subcategory
        }));
      }

      console.debug('Menu items processed:', {
        totalItems: menuItemsData?.length || 0,
        validItems: processedItems.length,
        withSubcategories: processedItems.filter(item => item.subcategory).length,
        sampleItem: processedItems[0]
      });

      // Update state with all data
      setCategories(categoriesData as Category[]);
      setSubcategories(subcategoriesData as Subcategory[]);
      setMenuItems(processedItems);

    } catch (error) {
      console.error('Error fetching menu data:', error);
      addToast({
        type: 'error',
        message: 'Failed to load menu data. Please try refreshing the page.'
      });
    } finally {
      setLoading(false);
    }
  };

  // Initial data fetch and real-time subscriptions
  useEffect(() => {
    if (isAuthLoading) return;

    console.debug('Setting up menu data subscriptions...');
    void fetchData();

    // Clean up previous subscription if it exists
    if (channelRef.current) {
      console.debug('Cleaning up previous subscription');
      channelRef.current.unsubscribe();
      channelRef.current = null;
    }

    // Set up real-time subscriptions
    const channel = supabase.channel('menu-changes');
    channelRef.current = channel;

    ['menu_items', 'categories', 'subcategories'].forEach(table => {
      channel.on(
        'postgres_changes',
        { event: '*', schema: 'public', table },
        (payload) => {
          console.debug(`${table} changed:`, payload);
          void fetchData();
        }
      );
    });

    channel.subscribe((status) => {
      console.debug('Subscription status:', status);
    });

    return () => {
      console.debug('Cleaning up subscriptions on unmount');
      if (channelRef.current) {
        channelRef.current.unsubscribe();
        channelRef.current = null;
      }
    };
  }, [addToast, isAuthLoading]);

  const validateItemData = (data: Omit<MenuItem, 'id' | 'created_at'>) => {
    // Validate required fields
    if (!data.name || !data.category_id) {
      throw new Error('Name and category are required');
    }

    // Validate category exists
    const category = categories.find(c => c.id === data.category_id);
    if (!category) {
      throw new Error('Selected category does not exist');
    }

    // Validate subcategory if provided
    if (data.subcategory_id) {
      const subcategory = subcategories.find(s => s.id === data.subcategory_id);
      if (!subcategory) {
        throw new Error('Selected subcategory does not exist');
      }
      // Verify subcategory belongs to selected category
      if (subcategory.category_id !== data.category_id) {
        throw new Error('Selected subcategory does not belong to the selected category');
      }
    }

    return true;
  };

  const createItem = async (data: Omit<MenuItem, 'id' | 'created_at'>) => {
    try {
      // Validate data before creating
      validateItemData(data);

      const { error } = await supabase
        .from('menu_items')
        .insert([{
          name: data.name,
          description: data.description,
          price: data.price,
          category_id: data.category_id,
          subcategory_id: data.subcategory_id,
          active: data.active
        }])
        .select()
        .single();

      if (error) throw error;

      addToast({
        type: 'success',
        message: 'Menu item created successfully',
      });
      void fetchData();
    } catch (error) {
      console.error('Error creating menu item:', error);
      addToast({
        type: 'error',
        message: error instanceof Error ? error.message : 'Failed to create menu item',
      });
      throw error;
    }
  };

  const updateItem = async (id: string, data: Omit<MenuItem, 'id' | 'created_at'>) => {
    try {
      // Validate data before updating
      validateItemData(data);

      const { error } = await supabase
        .from('menu_items')
        .update({
          name: data.name,
          description: data.description,
          price: data.price,
          category_id: data.category_id,
          subcategory_id: data.subcategory_id,
          active: data.active
        })
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;

      addToast({
        type: 'success',
        message: 'Menu item updated successfully',
      });
      void fetchData();
    } catch (error) {
      console.error('Error updating menu item:', error);
      addToast({
        type: 'error',
        message: error instanceof Error ? error.message : 'Failed to update menu item',
      });
      throw error;
    }
  };

  const deleteItem = async (id: string) => {
    try {
      const { error } = await supabase
        .from('menu_items')
        .delete()
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;

      addToast({
        type: 'success',
        message: 'Menu item deleted successfully',
      });
      void fetchData();
    } catch (error) {
      console.error('Error deleting menu item:', error);
      addToast({
        type: 'error',
        message: 'Failed to delete menu item',
      });
      throw error;
    }
  };

  const toggleItemAvailability = async (item: MenuItem) => {
    try {
      const { error } = await supabase
        .from('menu_items')
        .update({ active: !item.active })
        .eq('id', item.id)
        .select()
        .single();

      if (error) throw error;

      addToast({
        type: 'success',
        message: `${item.name} is now ${!item.active ? 'available' : 'unavailable'}`,
      });
      void fetchData();
    } catch (error) {
      console.error('Error updating item availability:', error);
      addToast({
        type: 'error',
        message: 'Failed to update item availability',
      });
      throw error;
    }
  };

  return {
    menuItems,
    categories,
    subcategories,
    loading,
    createItem,
    updateItem,
    deleteItem,
    toggleItemAvailability
  };
}